import type {
  ActionResult,
  PermissionRequest,
  RightsList,
  RightsRow,
  RightsSettings,
} from "@brandwall/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { ApiError, api, copyText, patch, post, timeAgo } from "../api";
import { useWorkspace } from "../components/Shell";
import { useToast } from "../components/Toast";

export const STATUS_LABEL: Record<string, string> = {
  draft: "Not asked yet",
  pending: "Asked",
  granted: "Said yes",
  replied: "Replied",
  declined: "Said no",
  expired: "No answer",
  withdrawn: "Changed their mind",
  trusted: "Trusted",
  own_post: "Your post",
  self_granted: "Said yes elsewhere",
};

const FILTERS = [
  "",
  "draft",
  "pending",
  "granted",
  "replied",
  "declined",
  "expired",
  "trusted",
] as const;

function route(r: RightsRow) {
  if (!r.request) return "Not needed";
  if (r.request.route === "manual") return "Noted by hand";
  return r.request.route === "comment" ? "Comment" : "Direct message";
}

export function details(r: RightsRow): string {
  const text = describe(r);
  const q = r.request;
  // The date the creator agreed to, or was asked about (D-099).
  if (!q?.showUntil || !text) return text;
  const day = new Date(`${q.showUntil}T00:00:00Z`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
  return `${text} · until ${day}`;
}

function describe(r: RightsRow): string {
  const q = r.request;
  if (r.status === "trusted") return "Someone you trust, so no need to ask";
  if (r.status === "own_post") return "Your own post, so no need to ask";
  if (!q) return "";
  // Asking for a longer yes: the yes before it still stands until its date.
  if (q.purpose === "extend") {
    if (q.status === "declined")
      return `Said no to a longer yes · ${timeAgo(q.answeredAt)}. Shown until its old date.`;
    if (q.status === "expired") return "No answer to a longer yes. Shown until its old date.";
    if (q.status === "pending")
      return `Asked to keep showing it ${timeAgo(q.sentAt)} · closes ${timeAgo(q.expiresAt)}`;
    if (q.status === "replied")
      return `Replied to a longer yes, but not with your yes hashtag: "${(q.answerText ?? "").slice(0, 60)}"`;
  }
  switch (q.status) {
    case "draft":
      return q.route === "comment" && !q.needsPerson
        ? q.scheduledAt && new Date(q.scheduledAt).getTime() > Date.now() + 60_000
          ? `Brandwall posts the comment ${timeAgo(q.scheduledAt)}, one at a time`
          : "Brandwall is posting the comment"
        : q.route === "comment"
          ? "Instagram didn't let Brandwall comment. Copy it and post it yourself."
          : "Ready to send. Copy the message and send it from Instagram.";
    case "pending":
      return `${q.route === "comment" ? "Asked by comment" : "Message sent"} ${timeAgo(q.sentAt)} · closes ${timeAgo(q.expiresAt)}`;
    case "granted":
      return q.answerVia === "hashtag"
        ? `Replied with your yes hashtag · ${timeAgo(q.answeredAt)}`
        : q.answerVia === "approval_page"
          ? `Tapped "Yes, I agree" · ${timeAgo(q.answeredAt)}`
          : q.answerVia === "ai_recorded"
            ? `Yes recorded by ${q.answeredBy ?? "your AI"} · ${timeAgo(q.answeredAt)}`
            : `Yes noted by hand${q.givenBy ? ` (${q.givenBy})` : ""} · ${timeAgo(q.answeredAt)}`;
    case "replied":
      return `Replied, but not with your yes hashtag: "${(q.answerText ?? "").slice(0, 60)}"`;
    case "declined":
      return `Tapped "No thanks" · ${timeAgo(q.answeredAt)}`;
    case "withdrawn":
      return `Took back their yes · ${timeAgo(q.answeredAt)}`;
    case "expired":
      return q.attempt === 1
        ? "No answer after 7 days · you can ask once more"
        : "No answer after asking twice";
  }
  return "";
}

export function RequestPanel({
  request,
  onChange,
  compact = false,
}: {
  request: PermissionRequest;
  onChange: (r: PermissionRequest) => void;
  /** Leaves out the post header when the page around it already shows the post. */
  compact?: boolean;
}) {
  const { ws } = useParams();
  const toast = useToast();
  const workspace = useWorkspace();
  const canAct = workspace.role !== "viewer";
  const [message, setMessage] = useState(request.message);
  const [grantOpen, setGrantOpen] = useState(false);
  const [givenBy, setGivenBy] = useState("");
  const [note, setNote] = useState("");
  const [reply, setReply] = useState("");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setMessage(request.message), [request.message]);

  const run = async (fn: () => Promise<PermissionRequest & { result?: ActionResult }>) => {
    try {
      const r = await fn();
      onChange(r);
      if (r.result) toast(r.result.summary);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Something went wrong.");
    }
  };

  const copy = async () => {
    if (message !== request.message) {
      await run(() => patch(`/v1/w/${ws}/requests/${request.id}`, { message }));
    }
    await copyText(message);
    if (request.status === "draft") {
      // Copying is the moment a person sends it: the request is now pending.
      await run(() => post(`/v1/w/${ws}/requests/${request.id}/sent`, {}));
    } else toast("Copied.");
  };

  const open =
    request.status === "draft" || request.status === "pending" || request.status === "replied";
  return (
    <div className="stack">
      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}
      <div className="row" hidden={compact}>
        {request.previewUrl && <img className="thumb lg" src={request.previewUrl} alt="" />}
        <div className="spacer">
          <b>{request.creator ? `@${request.creator}` : "Creator not known yet"}</b>
          <div className="small muted">
            {request.route === "comment"
              ? "By comment"
              : request.route === "dm"
                ? "By direct message"
                : "Noted by hand"}{" "}
            · {request.language.toUpperCase()} ·{" "}
            {request.attempt === 2 ? "second ask" : "first ask"}
          </div>
        </div>
        <span className={`status ${request.status}`}>{STATUS_LABEL[request.status]}</span>
      </div>

      {request.route !== "manual" && (
        <label className="field">
          <span className="row">
            <span className="spacer">
              {request.route === "comment" ? "Comment" : "Permission message"}
            </span>
            <span className="small muted" style={{ fontWeight: 500 }}>
              Written by Brandwall · you can edit it
            </span>
          </span>
          <textarea
            className="input"
            rows={5}
            value={message}
            disabled={!canAct || request.status !== "draft"}
            onChange={(e) => setMessage(e.target.value)}
          />
        </label>
      )}

      {canAct && open && (
        <div className="row wrap">
          {request.route !== "manual" && (request.status === "draft" || request.needsPerson) && (
            <button type="button" className="btn" onClick={copy}>
              {request.status === "draft" ? "Copy and mark as sent" : "Copy"}
            </button>
          )}
          {request.permalink && (
            <a className="btn secondary" href={request.permalink} target="_blank" rel="noreferrer">
              Open on Instagram
            </a>
          )}
          <button type="button" className="btn secondary" onClick={() => setGrantOpen(!grantOpen)}>
            Record a yes
          </button>
          {request.status !== "draft" && (
            <button
              type="button"
              className="btn link small"
              onClick={() => {
                if (
                  !window.confirm(
                    `Record that ${request.creator ? `@${request.creator}` : "the creator"} said no? The post comes off your walls.`,
                  )
                )
                  return;
                run(() => post(`/v1/w/${ws}/requests/${request.id}/decline`, {}));
              }}
            >
              Record a no
            </button>
          )}
        </div>
      )}

      {grantOpen && (
        <div className="card stack" style={{ background: "#fafaf6" }}>
          <p className="small">
            Use this when the creator said yes another way, for example by email or in a contract.
          </p>
          <label className="field">
            Who gave permission?
            <input
              className="input"
              value={givenBy}
              onChange={(e) => setGivenBy(e.target.value)}
              placeholder="Sofie, by email"
            />
          </label>
          <label className="field">
            Note
            <input
              className="input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional"
            />
          </label>
          <button
            type="button"
            className="btn ok"
            disabled={!givenBy.trim()}
            onClick={() =>
              run(() =>
                post(`/v1/w/${ws}/requests/${request.id}/grant`, {
                  givenBy,
                  note: note || undefined,
                }),
              ).then(() => setGrantOpen(false))
            }
          >
            Save permission
          </button>
        </div>
      )}

      {canAct && request.status === "pending" && (
        <details>
          <summary className="small" style={{ cursor: "pointer", fontWeight: 600 }}>
            The creator answered somewhere else
          </summary>
          <div className="stack" style={{ marginTop: 10 }}>
            <textarea
              className="input"
              rows={2}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Paste their exact words"
            />
            <button
              type="button"
              className="btn secondary sm"
              disabled={!reply.trim()}
              onClick={() =>
                run(() => post(`/v1/w/${ws}/requests/${request.id}/reply`, { text: reply }))
              }
            >
              Save their reply
            </button>
          </div>
        </details>
      )}

      {canAct && request.canAskAgain && (
        <button
          type="button"
          className="btn secondary"
          onClick={() => run(() => post(`/v1/w/${ws}/requests/${request.id}/again`, {}))}
        >
          Ask once more
        </button>
      )}

      <div className="stack" style={{ gap: 6 }}>
        <span className="label">Proof of permission</span>
        <dl
          className="small"
          style={{ margin: 0, display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 12px" }}
        >
          <dt className="muted">Asked</dt>
          <dd style={{ margin: 0 }}>
            {request.sentAt
              ? `${new Date(request.sentAt).toLocaleString("en-GB")}${request.sentBy ? ` by ${request.sentBy}` : ""}`
              : "Not yet"}
          </dd>
          <dt className="muted">Answer</dt>
          <dd style={{ margin: 0 }}>
            {request.answeredAt
              ? `${STATUS_LABEL[request.status]}, ${new Date(request.answeredAt).toLocaleString("en-GB")}`
              : "No answer yet"}
          </dd>
          {request.answerText && (
            <>
              <dt className="muted">Their words</dt>
              <dd style={{ margin: 0 }}>"{request.answerText}"</dd>
            </>
          )}
          {request.answerLink && (
            <>
              <dt className="muted">Link</dt>
              <dd style={{ margin: 0 }}>
                <a href={request.answerLink} target="_blank" rel="noreferrer">
                  See the reply
                </a>
              </dd>
            </>
          )}
          {request.givenBy && (
            <>
              <dt className="muted">Given by</dt>
              <dd style={{ margin: 0 }}>{request.givenBy}</dd>
            </>
          )}
          {request.note && (
            <>
              <dt className="muted">Note</dt>
              <dd style={{ margin: 0 }}>{request.note}</dd>
            </>
          )}
          {request.link && (
            <>
              <dt className="muted">Their yes page</dt>
              <dd style={{ margin: 0, wordBreak: "break-all" }}>
                <a href={request.link} target="_blank" rel="noreferrer">
                  {request.link.replace(/^https?:\/\//, "")}
                </a>
              </dd>
            </>
          )}
        </dl>
      </div>
    </div>
  );
}

const tagsOf = (r: RightsSettings) => r.hashtags.map((t) => `#${t}`).join(" ");

/** How long a new yes lasts (D-099): no limit, some months, or until a date. */
function ShowUntilSetting({
  value,
  disabled,
  save,
}: {
  value: RightsSettings["showUntil"];
  disabled: boolean;
  save: (v: RightsSettings["showUntil"]) => void;
}) {
  const isDate = /^\d{4}-/.test(value);
  const [pickDate, setPickDate] = useState(isDate);
  const [date, setDate] = useState(isDate ? value : "");
  useEffect(() => {
    setPickDate(/^\d{4}-/.test(value));
    if (/^\d{4}-/.test(value)) setDate(value);
  }, [value]);
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="stack" style={{ gap: 6 }}>
      <div className="row wrap" style={{ gap: 8 }}>
        <span className="spacer" style={{ fontWeight: 700 }}>
          How long a new yes lasts
        </span>
        <select
          className="input"
          style={{ width: "auto" }}
          disabled={disabled}
          aria-label="How long a new yes lasts"
          value={pickDate ? "date" : value}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "date") setPickDate(true);
            else {
              setPickDate(false);
              save(v as RightsSettings["showUntil"]);
            }
          }}
        >
          <option value="none">No limit</option>
          <option value="3m">3 months</option>
          <option value="6m">6 months</option>
          <option value="12m">12 months</option>
          <option value="date">Until a date</option>
        </select>
        {pickDate && (
          <>
            <input
              className="input"
              type="date"
              style={{ width: "auto" }}
              disabled={disabled}
              aria-label="Until"
              min={today}
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
            <button
              type="button"
              className="btn sm"
              disabled={disabled || !date || date === value || date <= today}
              onClick={() => save(date as RightsSettings["showUntil"])}
            >
              Save date
            </button>
          </>
        )}
      </div>
      {isDate && value <= today && (
        <span className="small" role="note">
          This date has passed, so new permission has no end date until you set a new one.
        </span>
      )}
      <span className="small muted">
        Brandwall tells creators the date when it asks. On that date the post leaves your walls by
        itself, and a week before, you're told so you can ask again. For contracts with ambassadors,
        set a date per trusted account on the Sources page.
      </span>
    </div>
  );
}

function Settings({ settings }: { settings: RightsSettings }) {
  const { ws } = useParams();
  const workspace = useWorkspace();
  const canEdit = workspace.role === "owner";
  const qc = useQueryClient();
  const toast = useToast();
  const [s, setS] = useState(settings);
  const [tagText, setTagText] = useState(tagsOf(settings));
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setS(settings);
    setTagText(tagsOf(settings));
  }, [settings]);
  const save = useMutation({
    mutationFn: (b: Partial<RightsSettings>) =>
      patch<RightsSettings>(`/v1/w/${ws}/rights/settings`, b),
    onSuccess: () => {
      toast("Saved how Brandwall asks.");
      setError(null);
      qc.invalidateQueries({ queryKey: ["rights", ws] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Something went wrong."),
  });
  const changed = JSON.stringify(s) !== JSON.stringify(settings);
  return (
    <section className="card stack">
      <div>
        <h2>How you ask</h2>
        <p className="muted">
          Brandwall writes to creators in their own language, and makes every request look like a
          person wrote it, because Instagram hides comments and can block accounts that look like a
          bot. Comments start with @username and a line about their photo, take turns between
          several wordings and your yes hashtags, never hold a link, and go out one every few
          minutes (at most 30 an hour).
        </p>
      </div>
      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}
      <label className="field">
        <span className="label">
          Yes hashtags, up to 3 (comments take turns; any of them is a yes)
        </span>
        <input
          className="input mono"
          value={tagText}
          disabled={!canEdit}
          placeholder="#yesfjellbu #fjellbuok"
          onChange={(e) => {
            setTagText(e.target.value);
            const tags = e.target.value
              .split(/[\s,]+/)
              .map((t) => t.replace(/^#/, "").toLowerCase())
              .filter(Boolean)
              .slice(0, 3);
            if (tags.length) setS({ ...s, hashtags: tags, hashtag: tags[0]! });
          }}
        />
      </label>
      <label className="field">
        <span className="label">Your own comment wording (optional)</span>
        <textarea
          className="input"
          rows={3}
          value={s.comment}
          disabled={!canEdit}
          placeholder="Could we feature this on our channels, with credit to you? Comment #{hashtag} to give us the go-ahead. Terms in our bio."
          onChange={(e) => setS({ ...s, comment: e.target.value })}
        />
        <span className="small muted" style={{ fontWeight: 500 }}>
          Joins Brandwall's six wordings in English. Use {"{hashtag}"} for the yes hashtag. Leave
          out links and words like reply, tag, share, page and website: Instagram reads them as a
          bot.
        </span>
      </label>
      <label className="field">
        <span className="label">Your own direct message (optional)</span>
        <textarea
          className="input"
          rows={4}
          value={s.dm}
          disabled={!canEdit}
          placeholder="Hi {name}! We love your post. May we show it on {site}, with credit to you? Tap here to say yes: {link}"
          onChange={(e) => setS({ ...s, dm: e.target.value })}
        />
        <span className="small muted" style={{ fontWeight: 500 }}>
          Joins Brandwall's four wordings in English. {"{name}"} is the creator's first name,{" "}
          {"{link}"} the page where they say yes, {"{site}"} your website.
        </span>
      </label>
      <label className="field">
        <span className="label">Link to your terms</span>
        <input
          className="input"
          value={s.termsUrl}
          disabled={!canEdit}
          placeholder="fjellbu.dk/ugc-terms"
          onChange={(e) => setS({ ...s, termsUrl: e.target.value })}
        />
        <span className="small muted" style={{ fontWeight: 500 }}>
          Put this link in your Instagram bio: comments say your terms are in your bio, since a link
          in a comment looks like spam. Messages and the yes page link to it.
        </span>
      </label>
      <label className="field">
        <span className="label">Sign off as</span>
        <input
          className="input"
          value={s.signature}
          disabled={!canEdit}
          onChange={(e) => setS({ ...s, signature: e.target.value })}
        />
      </label>
      {canEdit && changed && (
        <button type="button" className="btn" onClick={() => save.mutate(s)}>
          Save
        </button>
      )}
      <div className="row">
        <div className="spacer">
          <div style={{ fontWeight: 700 }}>Ask by comment when someone tags or mentions you</div>
          <div className="small muted">
            {s.askByComment
              ? "Brandwall writes your request as a comment on their post, from your brand's account, and reads their reply there."
              : "Brandwall never writes on anyone's posts. Every request is a message you send yourself on Instagram."}
          </div>
        </div>
        <label className="switch">
          <input
            type="checkbox"
            checked={s.askByComment}
            disabled={!canEdit}
            aria-label="Ask by comment when someone tags or mentions you"
            onChange={(e) => {
              setS({ ...s, askByComment: e.target.checked });
              save.mutate({ askByComment: e.target.checked });
            }}
          />
          <span />
        </label>
      </div>
      <div className="row">
        <span className="spacer" style={{ fontWeight: 700 }}>
          No need to ask for your own posts or people you trust
        </span>
        <label className="switch">
          <input
            type="checkbox"
            checked
            disabled
            aria-label="No need to ask for your own posts or people you trust"
          />
          <span />
        </label>
      </div>
      <div className="row">
        <span className="spacer" style={{ fontWeight: 700 }}>
          Other posts need a yes before they show on your walls
        </span>
        <label className="switch">
          <input
            type="checkbox"
            checked
            disabled
            aria-label="Other posts need a yes before they show on your walls"
          />
          <span />
        </label>
      </div>
      <ShowUntilSetting
        value={settings.showUntil}
        disabled={!canEdit}
        save={(showUntil) => {
          setS({ ...s, showUntil });
          save.mutate({ showUntil });
        }}
      />
      <p className="small muted">
        Brandwall never sends direct messages for you. You, or your own AI assistant, send them from
        your Instagram.
      </p>
    </section>
  );
}

export function Rights() {
  const { ws } = useParams();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const filter = params.get("status") ?? "";
  const selected = params.get("r");
  const list = useQuery({
    queryKey: ["rights", ws],
    queryFn: () => api<RightsList>(`/v1/w/${ws}/rights`),
  });
  const current = list.data?.rows.find((r) => r.request?.id === selected)?.request;
  const rows = (list.data?.rows ?? []).filter(
    (r) => !filter || r.status === filter || (filter === "trusted" && r.status === "own_post"),
  );
  const set = (k: string, v: string | null) => {
    const n = new URLSearchParams(params);
    if (v) n.set(k, v);
    else n.delete(k);
    setParams(n, { replace: true });
  };

  if (!list.data) return <p className="muted">Loading…</p>;
  const c = list.data.counts;
  return (
    <div className="split">
      <div className="stack" style={{ minWidth: 0 }}>
        <div className="filters" role="group" aria-label="Filter by status">
          {FILTERS.map((f) => {
            const n =
              f === ""
                ? c.all
                : f === "trusted"
                  ? (c.trusted ?? 0) + (c.own_post ?? 0)
                  : (c[f] ?? 0);
            if (f === "draft" && !n) return null;
            return (
              <button
                key={f}
                type="button"
                className="filter"
                aria-pressed={filter === f}
                onClick={() => set("status", f || null)}
              >
                {f ? STATUS_LABEL[f] : "All"} <span className="c">{n}</span>
              </button>
            );
          })}
        </div>
        <div className="card" style={{ padding: "14px 10px", overflowX: "auto" }}>
          {rows.length === 0 ? (
            <p className="muted" style={{ padding: 10 }}>
              Nothing here yet. Approve a post in your inbox, and asking its creator for permission
              starts here.
            </p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Post</th>
                  <th className="hide-sm">How</th>
                  <th>Status</th>
                  <th className="hide-sm">Details</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={`${r.postId}-${r.request?.id ?? r.status}`}
                    className={`${r.request ? "click" : ""} ${r.request?.id === selected ? "on" : ""}`}
                    onClick={() => r.request && set("r", r.request.id)}
                  >
                    <td>
                      <div className="row">
                        {r.previewUrl ? (
                          <img className="thumb" src={r.previewUrl} alt="" />
                        ) : (
                          <span className="thumb" />
                        )}
                        {r.request ? (
                          <button
                            type="button"
                            className="btn link"
                            style={{ textDecoration: "none", fontWeight: 700 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              set("r", r.request!.id);
                            }}
                          >
                            {r.creator ? `@${r.creator}` : "Unknown creator"}
                          </button>
                        ) : (
                          <b>@{r.creator}</b>
                        )}
                      </div>
                    </td>
                    <td className="hide-sm muted">{route(r)}</td>
                    <td>
                      <span className={`status ${r.status}`}>{STATUS_LABEL[r.status]}</span>
                    </td>
                    <td className="hide-sm muted small">{details(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <p className="small muted">
          Creators asked today: {list.data.sentToday} · this week: {list.data.sentThisWeek}
          {list.data.pace.commentsQueued > 0 &&
            ` · ${list.data.pace.commentsQueued} ${list.data.pace.commentsQueued === 1 ? "comment" : "comments"} waiting, the next ${timeAgo(list.data.pace.nextCommentAt)}`}
        </p>
        {list.data.pace.messagesLastHour >= 15 && (
          <div className="notice">
            You've sent {list.data.pace.messagesLastHour} messages in the last hour. Instagram may
            slow down accounts that send more than about 20 an hour, so take a break before sending
            more.
          </div>
        )}
      </div>
      <div className="stack sticky-side">
        {current && (
          <section className="card">
            <div className="row" style={{ marginBottom: 10 }}>
              <h2 className="spacer">Asking for permission</h2>
              <button type="button" className="btn link small" onClick={() => set("r", null)}>
                Close
              </button>
            </div>
            <RequestPanel
              request={current}
              onChange={() => qc.invalidateQueries({ queryKey: ["rights", ws] })}
            />
          </section>
        )}
        <Settings settings={list.data.settings} />
      </div>
    </div>
  );
}
